import { useEffect, useState, type FormEvent } from "react"
import { useParams } from "react-router"
import { client } from "@client/client"
import { getErrorMessage } from "@client/lib/errors"
import { Field, PageHero, SuccessCard, TextAreaField, publicInputClasses } from "./components"

interface PublicFormField {
  id: string
  type: "text" | "email" | "phone" | "textarea" | "select" | "checkbox" | "date"
  label: string
  name: string
  placeholder?: string
  required: boolean
  options?: string[]
  position: number
}

interface PublicForm {
  id: string
  code: string
  name: string
  description: string | null
  fields: PublicFormField[]
  submitButtonText: string
  successMessage: string
}

type FormValues = Record<string, string | boolean>

export function PublicFormPage() {
  const { code } = useParams<{ code: string }>()
  const [form, setForm] = useState<PublicForm | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [status, setStatus] = useState<"idle" | "submitting" | "success">("idle")
  const [values, setValues] = useState<FormValues>({})

  const resetForm = () => {
    if (!form) return
    setValues(Object.fromEntries(form.fields.map((field) => [field.name, field.type === "checkbox" ? false : ""])))
    setStatus("idle")
    setError(null)
  }

  useEffect(() => {
    if (!code) return
    let cancelled = false

    const load = async () => {
      try {
        const response = await client.api.public.forms[":code"].$get({ param: { code } })
        const body = await response.json()
        if (!response.ok || !("data" in body)) {
          throw new Error(getErrorMessage(body, "This form is not available."))
        }
        if (!cancelled) {
          const nextForm = body.data as PublicForm
          setForm(nextForm)
          setValues(
            Object.fromEntries(
              nextForm.fields.map((field) => [field.name, field.type === "checkbox" ? false : ""]),
            ),
          )
        }
      } catch (loadError) {
        if (!cancelled) setError(loadError instanceof Error ? loadError.message : "This form is not available.")
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    void load()
    return () => {
      cancelled = true
    }
  }, [code])

  const updateValue = (name: string, value: string | boolean) => {
    setValues((current) => ({ ...current, [name]: value }))
  }

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!code || !form || status === "submitting") return
    setStatus("submitting")
    setError(null)

    try {
      const response = await client.api.public.forms[":code"].$post({
        param: { code },
        json: values,
      })
      const body = await response.json()
      if (!response.ok) throw new Error(getErrorMessage(body, "Could not submit the form."))
      setStatus("success")
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : "Could not submit the form.")
      setStatus("idle")
    }
  }

  if (loading) {
    return (
      <section className="bg-cream-50 py-20">
        <div className="public-container space-y-5">
          <div className="mx-auto h-10 max-w-xl animate-pulse rounded bg-cream-200" />
          <div className="mx-auto h-4 max-w-2xl animate-pulse rounded bg-cream-200" />
          <div className="mx-auto h-96 max-w-2xl animate-pulse rounded-2xl bg-white" />
        </div>
      </section>
    )
  }

  if (error && !form) {
    return (
      <section className="bg-cream-50 py-20">
        <div className="public-container">
          <div className="mx-auto max-w-2xl rounded-2xl border border-gold-500 bg-white p-8 text-center">
            <h1 className="font-display text-2xl font-bold text-ink">Form unavailable</h1>
            <p className="mt-3 text-sm text-ink/70">{error}</p>
          </div>
        </div>
      </section>
    )
  }

  if (!form) return null

  if (status === "success") {
    return (
      <section className="bg-cream-50 py-20">
        <div className="public-container">
          <SuccessCard eyebrow="Form submitted" title="Thank you for taking the time." message={form.successMessage} onReset={resetForm} />
        </div>
      </section>
    )
  }

  return (
    <>
      <PageHero
        eyebrow="Public form"
        eyebrowDot
        title={<>{form.name}</>}
        lede={form.description ?? "Complete the form below and our team will be in touch."}
      />
      <section className="bg-cream-50 px-4 pb-20 pt-8 sm:px-6 lg:pt-12">
        <form onSubmit={submit} className="public-container mx-auto max-w-2xl rounded-2xl bg-white p-5 shadow-sm sm:p-8">
          <div className="space-y-5">
            {[...form.fields].sort((a, b) => a.position - b.position).map((field) => {
              const value = values[field.name] ?? ""
              const disabled = status === "submitting"

              if (field.type === "textarea") {
                return (
                  <TextAreaField
                    key={field.id}
                    label={field.label}
                    name={field.name}
                    value={String(value)}
                    onChange={(event) => updateValue(field.name, event.target.value)}
                    placeholder={field.placeholder}
                    required={field.required}
                    disabled={disabled}
                  />
                )
              }

              if (field.type === "select") {
                return (
                  <label key={field.id} className="block">
                    <span className="mb-1.5 block text-base font-medium text-ink sm:mb-2 sm:text-lg">{field.label}</span>
                    <select
                      name={field.name}
                      value={String(value)}
                      onChange={(event) => updateValue(field.name, event.target.value)}
                      required={field.required}
                      disabled={disabled}
                      className={publicInputClasses}
                    >
                      <option value="">Select an option</option>
                      {(field.options ?? []).map((option) => <option key={option} value={option}>{option}</option>)}
                    </select>
                  </label>
                )
              }

              if (field.type === "checkbox") {
                return (
                  <label key={field.id} className="flex items-start gap-3 text-sm text-ink/80">
                    <input
                      type="checkbox"
                      name={field.name}
                      checked={Boolean(value)}
                      onChange={(event) => updateValue(field.name, event.target.checked)}
                      required={field.required}
                      disabled={disabled}
                      className="mt-1 h-4 w-4 accent-forest-950"
                    />
                    <span>{field.label}</span>
                  </label>
                )
              }

              return (
                <Field
                  key={field.id}
                  label={field.label}
                  name={field.name}
                  type={field.type}
                  value={String(value)}
                  onChange={(event) => updateValue(field.name, event.target.value)}
                  placeholder={field.placeholder}
                  required={field.required}
                  disabled={disabled}
                />
              )
            })}
          </div>
          {error && <p className="mt-5 text-sm font-medium text-red-700">{error}</p>}
          <button
            type="submit"
            disabled={status === "submitting"}
            className="mt-7 w-full rounded-full bg-forest-950 py-4 text-base font-semibold text-white transition-colors hover:bg-forest-800 disabled:opacity-60"
          >
            {status === "submitting" ? "Sending..." : form.submitButtonText}
          </button>
        </form>
      </section>
    </>
  )
}
